// DEPENDENCIES ---------------------------------------------------------------- //

import dayjs from 'dayjs';

import {
  AppointmentManagementConfig,
  ReportingBusinessHours,
  ReportingMove,
  ReportingMoveAudit,
} from '../types';
import { listDaysInRange } from './reportingDates';

// NOTE: Move date bucketing uses browser-local time for v1.
// TODO: Bucket by the customer location's timezone using the dayjs timezone
// plugin (already loaded in main.tsx) once reporting needs cross-timezone accuracy.

// CONSTANTS ---------------------------------------------------------------- //

/** Statuses that imply a driver has been assigned to the move */
const ASSIGNED_STATUSES = [
  'dispatched',
  'pickup started',
  'pickup arrived',
  'pickup successful',
  'delivery started',
  'delivery arrived',
  'delivery successful',
];

/** Fallback average move duration (minutes) when no lane durations exist */
export const FALLBACK_MOVE_DURATION_MINUTES = 45;

/** Minimum slot duration (minutes) to avoid divide-by-zero or bad config */
export const MIN_SLOT_DURATION_MINUTES = 15;

/** Weekday labels ordered Monday through Sunday for charts */
const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// SHARED HELPERS ---------------------------------------------------------------- //

/** A move is canceled when any cancel status is present */
export function isCanceled(move: ReportingMove): boolean {
  return move.cancel_status != null;
}

/** A move is completed when delivery was marked successful (timestamp set) or
 *  status reached "delivery successful". Cancellation overrides completion. */
export function isCompleted(move: ReportingMove): boolean {
  return !isCanceled(move) && (move.delivery_successful != null || move.status === 'delivery successful');
}

/** A move is pending when it is not canceled/completed and its pickup time is
 *  in the future (or it has no pickup time yet) */
export function isPending(move: ReportingMove, now: Date = new Date()): boolean {
  return (
    !isCanceled(move) &&
    !isCompleted(move) &&
    (!move.pickup_time || new Date(move.pickup_time).getTime() >= now.getTime())
  );
}

/** A move is in progress when it is past pickup time but not finished. Bucket
 *  for "other" in status distributions. */
export function isInProgressOrOther(move: ReportingMove, now: Date = new Date()): boolean {
  return !isCanceled(move) && !isCompleted(move) && !isPending(move, now);
}

/** A move is assigned when a driver is attached or its status implies one.
 *  Be conservative: unknown fields do not count as assigned. */
export function isAssigned(move: ReportingMove): boolean {
  return (
    !isCanceled(move) &&
    (move.driver_id != null || !!move.driver_name || ASSIGNED_STATUSES.includes(move.status || ''))
  );
}

/** The representative date of a move for bucketing: pickup time, else ready by */
export function getMoveDate(move: ReportingMove): string | null {
  return move.pickup_time || move.ready_by || null;
}

/** Normalize a phone for consumer grouping: digits only, strip leading 1 */
function normalizePhone(phone?: string | null): string | null {
  if (!phone) return null;
  let digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  return digits.length >= 7 ? digits : null;
}

/** Normalize a consumer name for grouping */
function normalizeName(name?: string | null): string | null {
  const normalized = (name || '').trim().toLowerCase().replace(/\s+/g, ' ');
  return normalized.length > 0 ? normalized : null;
}

/** Grouping key for a driver: prefer driver_id, else non-empty driver_name */
function getDriverKey(move: ReportingMove): string | null {
  if (move.driver_id != null) return `id:${move.driver_id}`;
  const name = (move.driver_name || '').trim();
  return name ? `name:${name.toLowerCase()}` : null;
}

/** Display label for a driver */
function getDriverLabel(move: ReportingMove): string {
  const name = (move.driver_name || '').trim();
  if (name) return name;
  if (move.driver_id != null) return `Driver ${move.driver_id}`;
  return 'Unknown driver';
}

/** Parse a DB time string (HH:mm or HH:mm:ss) into minutes after midnight */
export function parseTimeToMinutes(time?: string | null): number | null {
  if (!time || typeof time !== 'string') return null;
  const match = time.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

// OPERATIONS METRICS ---------------------------------------------------------------- //

export type OperationsMetrics = {
  totalMoves: number;
  completed: number;
  pending: number;
  canceled: number;
  /** 0-1 fraction, null when no completed moves have both delivery_time and deliver_by */
  onTimePercent: number | null;
  onTimeDenominator: number;
  /** null when no moves have a lane distance */
  avgDistanceMiles: number | null;
  activeDrivers: number;
  /** null when there are no active drivers */
  movesPerDriver: number | null;
  repeatCustomers: number;
  /** null when reference numbers are not required for this customer */
  missingRefCount: number | null;
  ridesharesUtilized: number;
};

export function buildOperationsMetrics(
  moves: ReportingMove[],
  options?: { refNumRequired?: boolean; now?: Date }
): OperationsMetrics {
  const now = options?.now || new Date();
  const refNumRequired = options?.refNumRequired === true;

  let completed = 0;
  let pending = 0;
  let canceled = 0;
  let onTimeNumerator = 0;
  let onTimeDenominator = 0;
  let distanceSum = 0;
  let distanceCount = 0;
  let missingRefCount = 0;
  let ridesharesUtilized = 0;

  const driverKeys = new Set<string>();
  const consumerCounts = new Map<string, number>();

  for (const move of moves) {
    const moveCanceled = isCanceled(move);
    const moveCompleted = isCompleted(move);

    if (moveCanceled) canceled++;
    else if (moveCompleted) completed++;
    else if (isPending(move, now)) pending++;

    // On-time: completed moves where both delivery_time and deliver_by exist
    if (moveCompleted && move.delivery_time && move.deliver_by) {
      onTimeDenominator++;
      if (new Date(move.delivery_time).getTime() <= new Date(move.deliver_by).getTime()) onTimeNumerator++;
    }

    // Average distance across moves with a lane distance
    const distance = move.lane?.distance_miles;
    if (distance != null && Number.isFinite(Number(distance))) {
      distanceSum += Number(distance);
      distanceCount++;
    }

    // Active drivers from non-canceled moves
    if (!moveCanceled) {
      const driverKey = getDriverKey(move);
      if (driverKey) driverKeys.add(driverKey);
    }

    // Repeat customers grouped by normalized phone first, then name
    const consumerKey = normalizePhone(move.consumer_phone) || normalizeName(move.consumer_name);
    if (consumerKey) consumerCounts.set(consumerKey, (consumerCounts.get(consumerKey) || 0) + 1);

    // Missing reference numbers (only meaningful when required)
    if (refNumRequired && !(move.reference_num || '').trim()) missingRefCount++;

    // Rideshares utilized: one or more active, non-canceled ride child moves
    if (move.childRideMoves?.some(ride => ride.cancel_status == null)) ridesharesUtilized++;
  }

  let repeatCustomers = 0;
  consumerCounts.forEach(count => {
    if (count > 1) repeatCustomers++;
  });

  const activeDrivers = driverKeys.size;
  const countedMoves = moves.length - canceled;

  return {
    totalMoves: moves.length,
    completed,
    pending,
    canceled,
    onTimePercent: onTimeDenominator > 0 ? onTimeNumerator / onTimeDenominator : null,
    onTimeDenominator,
    avgDistanceMiles: distanceCount > 0 ? distanceSum / distanceCount : null,
    activeDrivers,
    movesPerDriver: activeDrivers > 0 ? countedMoves / activeDrivers : null,
    repeatCustomers,
    missingRefCount: refNumRequired ? missingRefCount : null,
    ridesharesUtilized,
  };
}

// CHART BUILDERS ---------------------------------------------------------------- //

export type MoveVolumeDay = {
  date: string;
  label: string;
  completed: number;
  pending: number;
  canceled: number;
  other: number;
};

/** Daily counts of completed/pending/canceled/other moves between start and end */
export function buildMoveVolumeByDay(
  moves: ReportingMove[],
  start: string,
  end: string,
  now: Date = new Date()
): MoveVolumeDay[] {
  const days = listDaysInRange(start, end);
  const byDay = new Map<string, MoveVolumeDay>(
    days.map(date => [
      date,
      { date, label: dayjs(date).format('MMM D'), completed: 0, pending: 0, canceled: 0, other: 0 },
    ])
  );

  for (const move of moves) {
    const moveDate = getMoveDate(move);
    if (!moveDate) continue;
    const bucket = byDay.get(dayjs(moveDate).format('YYYY-MM-DD'));
    if (!bucket) continue;

    if (isCanceled(move)) bucket.canceled++;
    else if (isCompleted(move)) bucket.completed++;
    else if (isPending(move, now)) bucket.pending++;
    else bucket.other++;
  }

  return days.map(date => byDay.get(date) as MoveVolumeDay);
}

export type StatusDistribution = {
  key: 'completed' | 'pending' | 'canceled' | 'inProgress';
  label: string;
  count: number;
};

/** Donut chart data: completed, pending, canceled, in progress/other */
export function buildStatusDistribution(moves: ReportingMove[], now: Date = new Date()): StatusDistribution[] {
  let completed = 0;
  let pending = 0;
  let canceled = 0;
  let inProgress = 0;

  for (const move of moves) {
    if (isCanceled(move)) canceled++;
    else if (isCompleted(move)) completed++;
    else if (isPending(move, now)) pending++;
    else inProgress++;
  }

  return [
    { key: 'completed', label: 'Completed', count: completed },
    { key: 'pending', label: 'Pending', count: pending },
    { key: 'canceled', label: 'Canceled', count: canceled },
    { key: 'inProgress', label: 'In Progress / Other', count: inProgress },
  ];
}

export type WeekdayCount = { label: string; count: number };

/** Move counts by weekday, Monday through Sunday, based on pickup_time || ready_by */
export function buildMovesByWeekday(moves: ReportingMove[]): WeekdayCount[] {
  const counts = new Array(7).fill(0); // index 0 = Monday

  for (const move of moves) {
    const moveDate = getMoveDate(move);
    if (!moveDate) continue;
    const date = dayjs(moveDate);
    if (!date.isValid()) continue;
    const mondayIndexed = (date.day() + 6) % 7; // dayjs: 0 = Sunday
    counts[mondayIndexed]++;
  }

  return WEEKDAY_LABELS.map((label, i) => ({ label, count: counts[i] }));
}

export type HourCount = { hour: number; label: string; count: number };

/** Move counts by hour of day based on pickup_time || ready_by */
export function buildMovesByHour(moves: ReportingMove[]): HourCount[] {
  const counts = new Array(24).fill(0);

  for (const move of moves) {
    const moveDate = getMoveDate(move);
    if (!moveDate) continue;
    const date = dayjs(moveDate);
    if (!date.isValid()) continue;
    counts[date.hour()]++;
  }

  return counts.map((count, hour) => ({
    hour,
    label: dayjs().hour(hour).minute(0).format('h A'),
    count,
  }));
}

export type DriverActivity = { key: string; label: string; count: number };

/** Top N drivers by non-canceled move count */
export function buildDriverActivity(moves: ReportingMove[], topN = 8): DriverActivity[] {
  const byDriver = new Map<string, DriverActivity>();

  for (const move of moves) {
    if (isCanceled(move)) continue;
    const key = getDriverKey(move);
    if (!key) continue;
    const existing = byDriver.get(key);
    if (existing) existing.count++;
    else byDriver.set(key, { key, label: getDriverLabel(move), count: 1 });
  }

  return Array.from(byDriver.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, topN);
}

export type DistanceBucket = { label: string; count: number };

const DISTANCE_BUCKETS: Array<{ label: string; min: number; max: number | null }> = [
  { label: '0-5 mi', min: 0, max: 5 },
  { label: '5-10 mi', min: 5, max: 10 },
  { label: '10-25 mi', min: 10, max: 25 },
  { label: '25-50 mi', min: 25, max: 50 },
  { label: '50+ mi', min: 50, max: null },
];

/** Distance distribution for moves with a lane distance */
export function buildDistanceDistribution(moves: ReportingMove[]): DistanceBucket[] {
  const counts = new Array(DISTANCE_BUCKETS.length).fill(0);

  for (const move of moves) {
    const distance = move.lane?.distance_miles;
    if (distance == null || !Number.isFinite(Number(distance))) continue;
    const miles = Number(distance);
    const index = DISTANCE_BUCKETS.findIndex(b => miles >= b.min && (b.max == null || miles < b.max));
    if (index >= 0) counts[index]++;
  }

  return DISTANCE_BUCKETS.map((bucket, i) => ({ label: bucket.label, count: counts[i] }));
}

// APPOINTMENT USAGE ---------------------------------------------------------------- //

export type AppointmentUsageDay = {
  date: string;
  label: string;
  /** Theoretical max appointments for the day (0 when closed/no hours) */
  capacity: number;
  /** Non-canceled drive moves with a pickup time on this day */
  scheduled: number;
  /** scheduled / capacity, null when capacity is 0 */
  utilization: number | null;
};

export type AppointmentUsage = {
  days: AppointmentUsageDay[];
  /** Sum of daily capacity */
  totalCapacity: number;
  /** Sum of daily scheduled moves */
  totalScheduled: number;
  /** totalScheduled / totalCapacity, null when capacity is 0 */
  estimatedUtilization: number | null;
  /** max(totalCapacity - totalScheduled, 0) */
  unusedSlots: number;
  /** Future moves within range, not canceled */
  scheduledMoves: number;
  completedAppointments: number;
  canceledAppointments: number;
  configuredDrivers: number;
  /** Non-canceled moves divided by configured drivers, null when drivers is 0 */
  avgMovesPerDriver: number | null;
  /** Average lane duration used for slot estimation (minutes) */
  avgMoveDurationMinutes: number;
  /** True when no completed lane durations existed and the fallback was used */
  usedFallbackDuration: boolean;
  /** True when at least one open business-hours record was available */
  hasBusinessHours: boolean;
};

export function buildAppointmentUsage(params: {
  moves: ReportingMove[];
  businessHours: ReportingBusinessHours[];
  config?: AppointmentManagementConfig | null;
  start: string;
  end: string;
  now?: Date;
}): AppointmentUsage {
  const { moves, businessHours, config, start, end } = params;
  const now = params.now || new Date();

  // Configured drivers: invalid/missing falls back to 1; an explicit 0 stays 0
  const rawDriverCount = config?.driver_count;
  const configuredDrivers =
    typeof rawDriverCount === 'number' && Number.isFinite(rawDriverCount) && rawDriverCount >= 0
      ? Math.floor(rawDriverCount)
      : 1;

  // Move buffer: invalid/missing falls back to 0
  const rawBuffer = config?.move_buffer_minutes;
  const moveBufferMinutes =
    typeof rawBuffer === 'number' && Number.isFinite(rawBuffer) && rawBuffer >= 0 ? rawBuffer : 0;

  // Average move duration from completed moves' lane durations, fallback 45 minutes
  let durationSum = 0;
  let durationCount = 0;
  for (const move of moves) {
    if (!isCompleted(move)) continue;
    const sec = move.lane?.duration_sec;
    if (sec != null && Number.isFinite(Number(sec)) && Number(sec) > 0) {
      durationSum += Number(sec) / 60;
      durationCount++;
    }
  }
  const usedFallbackDuration = durationCount === 0;
  const avgMoveDurationMinutes = usedFallbackDuration ? FALLBACK_MOVE_DURATION_MINUTES : durationSum / durationCount;

  const slotDuration = Math.max(avgMoveDurationMinutes + moveBufferMinutes, MIN_SLOT_DURATION_MINUTES);

  // Index open business hours by day of week (first open record per day wins)
  const hoursByDow = new Map<number, ReportingBusinessHours>();
  for (const bh of businessHours) {
    if (bh.day_of_week == null) continue;
    if (bh.is_open === false) continue;
    if (!hoursByDow.has(bh.day_of_week)) hoursByDow.set(bh.day_of_week, bh);
  }
  const hasBusinessHours = hoursByDow.size > 0;

  // Scheduled (non-canceled) move counts by local day
  const scheduledByDay = new Map<string, number>();
  for (const move of moves) {
    if (isCanceled(move) || !move.pickup_time) continue;
    const day = dayjs(move.pickup_time).format('YYYY-MM-DD');
    scheduledByDay.set(day, (scheduledByDay.get(day) || 0) + 1);
  }

  // Daily capacity and utilization
  const days: AppointmentUsageDay[] = listDaysInRange(start, end).map(date => {
    const dow = dayjs(date).day(); // 0 = Sunday, matching businesshours.day_of_week
    const bh = hoursByDow.get(dow);

    let capacity = 0;
    if (bh) {
      const open = parseTimeToMinutes(bh.open_time);
      const closed = parseTimeToMinutes(bh.closed_time);
      const operatingMinutes = open != null && closed != null ? closed - open : 0;
      if (operatingMinutes > 0) capacity = Math.floor(operatingMinutes / slotDuration) * configuredDrivers;
    }

    const scheduled = scheduledByDay.get(date) || 0;

    return {
      date,
      label: dayjs(date).format('MMM D'),
      capacity,
      scheduled,
      utilization: capacity > 0 ? scheduled / capacity : null,
    };
  });

  const totalCapacity = days.reduce((sum, d) => sum + d.capacity, 0);
  const totalScheduled = days.reduce((sum, d) => sum + d.scheduled, 0);

  // Card metrics
  let scheduledMoves = 0;
  let completedAppointments = 0;
  let canceledAppointments = 0;
  let nonCanceled = 0;
  for (const move of moves) {
    if (isCanceled(move)) {
      canceledAppointments++;
      continue;
    }
    nonCanceled++;
    if (isCompleted(move)) completedAppointments++;
    if (move.pickup_time && new Date(move.pickup_time).getTime() >= now.getTime()) scheduledMoves++;
  }

  return {
    days,
    totalCapacity,
    totalScheduled,
    estimatedUtilization: totalCapacity > 0 ? totalScheduled / totalCapacity : null,
    unusedSlots: Math.max(totalCapacity - totalScheduled, 0),
    scheduledMoves,
    completedAppointments,
    canceledAppointments,
    configuredDrivers,
    avgMovesPerDriver: configuredDrivers > 0 ? nonCanceled / configuredDrivers : null,
    avgMoveDurationMinutes,
    usedFallbackDuration,
    hasBusinessHours,
  };
}

// EXECUTIVE KPIS ---------------------------------------------------------------- //

export type ExecutiveKpis = {
  totalMoves: number;
  assignedCount: number;
  /** 0-1 fraction, null when there are no moves */
  assignedPercent: number | null;
  completedCount: number;
  /** 0-1 fraction, null when there are no moves */
  completedPercent: number | null;
  validatedCount: number;
  /** Completed moves that have an audit record */
  auditedCompletedCount: number;
  /** 0-1 fraction, null when no completed moves have audit records */
  validatedPercent: number | null;
  /** Validation breakdown among audited completed moves */
  validationBreakdown: {
    pickupGpsValid: number;
    deliveryGpsValid: number;
    durationValid: number;
  };
};

/** Index audits by move_id, keeping the first audit per move */
function indexAuditsByMoveId(audits: ReportingMoveAudit[]): Map<number, ReportingMoveAudit> {
  const byMoveId = new Map<number, ReportingMoveAudit>();
  for (const audit of audits) {
    if (audit.move_id == null) continue;
    if (!byMoveId.has(audit.move_id)) byMoveId.set(audit.move_id, audit);
  }
  return byMoveId;
}

/** A move is validated when all three audit checks pass */
function isAuditValidated(audit: ReportingMoveAudit): boolean {
  return (
    audit.pickup_location_within_bounds === true &&
    audit.delivery_location_within_bounds === true &&
    audit.move_duration_within_bounds === true
  );
}

export function buildExecutiveKpis(moves: ReportingMove[], audits: ReportingMoveAudit[]): ExecutiveKpis {
  const auditsByMoveId = indexAuditsByMoveId(audits);

  const totalMoves = moves.length;
  let assignedCount = 0;
  let completedCount = 0;
  let auditedCompletedCount = 0;
  let validatedCount = 0;
  let pickupGpsValid = 0;
  let deliveryGpsValid = 0;
  let durationValid = 0;

  for (const move of moves) {
    if (isAssigned(move)) assignedCount++;

    if (isCompleted(move)) {
      completedCount++;

      const audit = auditsByMoveId.get(move.id);
      if (audit) {
        auditedCompletedCount++;
        if (isAuditValidated(audit)) validatedCount++;
        if (audit.pickup_location_within_bounds === true) pickupGpsValid++;
        if (audit.delivery_location_within_bounds === true) deliveryGpsValid++;
        if (audit.move_duration_within_bounds === true) durationValid++;
      }
    }
  }

  return {
    totalMoves,
    assignedCount,
    assignedPercent: totalMoves > 0 ? assignedCount / totalMoves : null,
    completedCount,
    completedPercent: totalMoves > 0 ? completedCount / totalMoves : null,
    validatedCount,
    auditedCompletedCount,
    validatedPercent: auditedCompletedCount > 0 ? validatedCount / auditedCompletedCount : null,
    validationBreakdown: {
      pickupGpsValid,
      deliveryGpsValid,
      durationValid,
    },
  };
}

export type KpiTrendDay = {
  date: string;
  label: string;
  /** 0-1 fractions, null when the day has no moves (or no audited completions for validated) */
  assignedPercent: number | null;
  completedPercent: number | null;
  validatedPercent: number | null;
  /** Raw daily counts so chart tooltips can show volume context on low-volume days */
  totalCount: number;
  assignedCount: number;
  completedCount: number;
  auditedCount: number;
  validatedCount: number;
};

/** Daily assigned/completed/validated percentages for the KPI trend chart */
export function buildKpiTrendByDay(
  moves: ReportingMove[],
  audits: ReportingMoveAudit[],
  start: string,
  end: string
): KpiTrendDay[] {
  const auditsByMoveId = indexAuditsByMoveId(audits);
  const days = listDaysInRange(start, end);

  type DayTotals = { total: number; assigned: number; completed: number; audited: number; validated: number };
  const totalsByDay = new Map<string, DayTotals>(
    days.map(date => [date, { total: 0, assigned: 0, completed: 0, audited: 0, validated: 0 }])
  );

  for (const move of moves) {
    const moveDate = getMoveDate(move);
    if (!moveDate) continue;
    const totals = totalsByDay.get(dayjs(moveDate).format('YYYY-MM-DD'));
    if (!totals) continue;

    totals.total++;
    if (isAssigned(move)) totals.assigned++;
    if (isCompleted(move)) {
      totals.completed++;
      const audit = auditsByMoveId.get(move.id);
      if (audit) {
        totals.audited++;
        if (isAuditValidated(audit)) totals.validated++;
      }
    }
  }

  return days.map(date => {
    const totals = totalsByDay.get(date) as DayTotals;
    return {
      date,
      label: dayjs(date).format('MMM D'),
      assignedPercent: totals.total > 0 ? totals.assigned / totals.total : null,
      completedPercent: totals.total > 0 ? totals.completed / totals.total : null,
      validatedPercent: totals.audited > 0 ? totals.validated / totals.audited : null,
      totalCount: totals.total,
      assignedCount: totals.assigned,
      completedCount: totals.completed,
      auditedCount: totals.audited,
      validatedCount: totals.validated,
    };
  });
}
